"use client";

import { useEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";
import {
  Button,
  Card,
  Flex,
  Icon,
  Image,
  Separator,
  Text,
} from "@highereducation/hen-ui";
import { getCompletionTimeText } from "@/components/NewProductCard/utils/getCompletionTimeText";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  NYI_NUMBER,
  NYI_STRING,
} from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import useProductEvents from "@/hooks/eventing/useProductEvents";
import { ProductClicked } from "@/hooks/eventing/types/redventures.ecommerce.v1.ProductClicked";
import { cn } from "@/lib/utils";
import { translateVariant } from "@/utils/tagular/dataTranslationMatrices";
import stripHtmlTags from "@/utils/stripHtmlTags";
import { createCustomDimensions } from "@/utils/tagular/createDimension";
import {
  getBrandStringFromPartnerKeys,
  getTagularSubject,
  hyphenateForTagular,
  setCorrelationId,
} from "@/utils/tagular/helpers";
import { CompactProduct } from "../types";
import useTranslations from "@/i18n/useTranslations";

interface CompactProductListItemProps {
  product: CompactProduct;
  sectionTitle: string;
  location?: string;
  index: number;
  isExpanded: boolean;
  onExpandToggle: () => void;
}

export function CompactProductListItem({
  product,
  sectionTitle,
  location,
  index,
  isExpanded,
  onExpandToggle,
}: CompactProductListItemProps) {
  const t = useTranslations("ProductCompare");

  const { ref: itemRef, inView: itemInView } = useInView(
    DEFAULT_USE_IN_VIEW_OPTIONS,
  );
  const { ref: ctaRef, inView: ctaInView } = useInView(
    DEFAULT_USE_IN_VIEW_OPTIONS,
  );
  // extra guards so that view events don't trigger again on expand toggle
  const [itemAlreadyViewed, setItemAlreadyViewed] = useState(false);
  const [ctaAlreadyViewed, setCtaAlreadyViewed] = useState(false);

  const duration = product.weeksToComplete
    ? (getCompletionTimeText(product.weeksToComplete, false) ?? "")
    : "";
  const flexibility = product.flexibility;
  const expandableId = `compact-product-details-${index}`;
  const chevronIcon = isExpanded ? "chevron-up" : "chevron-down";

  const filteredShortDescription = product.shortDescription
    ? stripHtmlTags(product.shortDescription)
    : "";

  const chevronWebElement = useMemo(
    () => ({
      elementType: "BUTTON",
      htmlId: `chevron-cta-${hyphenateForTagular(sectionTitle)}`,
      location,
      name: "chevron-cta",
    }),
    [location, sectionTitle],
  );

  const ctaWebElement = useMemo(
    () => ({
      elementType: "BUTTON",
      htmlId: `learn-more-${hyphenateForTagular(sectionTitle)}`,
      location,
      name: "learn-more",
      text: "Learn more",
    }),
    [location, sectionTitle],
  );

  const { elementClicked: chevronClicked } = useElementEvents({
    webElement: chevronWebElement,
  });

  const { elementViewed: ctaViewed } = useElementEvents({
    webElement: ctaWebElement,
  });

  const tagularProduct: ProductClicked["product"] = useMemo(() => {
    const partnerKeyEvent = product.partnerKeys || product.partnerName;
    return {
      brand: getBrandStringFromPartnerKeys(
        Array.isArray(partnerKeyEvent)
          ? partnerKeyEvent
          : [partnerKeyEvent ?? ""],
      ),
      formatSubtype: `horizontal-stacked-${hyphenateForTagular(sectionTitle)}`,
      formatType: "product-card",
      location: location || NYI_STRING,
      name: product.productName,
      variant: translateVariant(product.productType),
      position: index < 0 ? NYI_NUMBER : index + 1,
      category: getTagularSubject(product.subjects),
      productId: product.productUuid,
    };
  }, [index, location, product, sectionTitle]);

  const customDimensions = useMemo(() => {
    return createCustomDimensions([
      ["duration", duration],
      ["flexibility", flexibility],
    ]);
  }, [duration, flexibility]);

  const { productClicked, productViewed, correlationId } = useProductEvents({
    product: tagularProduct,
    customDimensions,
  });

  const handleExpandToggle = () => {
    onExpandToggle();
    chevronClicked();
  };

  const handleProductClick = () => {
    setCorrelationId(correlationId);
    productClicked();
  };

  useEffect(() => {
    if (itemInView && !itemAlreadyViewed) {
      productViewed();
      setItemAlreadyViewed(true);
    }
  }, [itemAlreadyViewed, itemInView, productViewed]);

  useEffect(() => {
    if (ctaInView && !ctaAlreadyViewed) {
      ctaViewed();
      setCtaAlreadyViewed(true);
    }
  }, [ctaInView, ctaViewed, ctaAlreadyViewed]);

  return (
    <Card
      variant="background-white"
      className="box-border w-full rounded-[.625rem] border border-neutral-200"
    >
      <button
        ref={itemRef}
        type="button"
        onClick={handleExpandToggle}
        aria-expanded={isExpanded}
        aria-controls={expandableId}
        aria-label={
          isExpanded
            ? `Hide details for ${product.productName}`
            : `Show details for ${product.productName}`
        }
        className="w-full cursor-pointer rounded-md border-0 bg-transparent p-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
      >
        <Flex
          direction="row"
          align="center"
          justify="start"
          className="min-h-[72px] w-full gap-2.5 p-3"
        >
          <Image
            src={product.partnerLogoUrl}
            alt={`${product.partnerName ?? product.productName} logo`}
            className="h-[42px] w-[73px] shrink-0 overflow-hidden rounded border border-neutral-200 object-contain p-0.5"
          />

          <Text
            variant="b4"
            weight="bold"
            className="m-0 flex-1 text-neutral-900 line-clamp-2 break-words"
          >
            {product.productName}
          </Text>

          <Icon
            icon={chevronIcon}
            strokeWidth={2}
            className="ml-auto -mt-1 size-6 shrink-0 self-start text-black"
            aria-hidden
          />
        </Flex>
      </button>

      <Flex
        id={expandableId}
        direction="column"
        className={cn("w-full gap-2.5 px-3 pb-3", !isExpanded && "hidden")}
      >
        <Separator className="bg-neutral-200" />

        {filteredShortDescription && (
          <Text
            tag="p"
            variant="b5"
            className="m-0 line-clamp-2 text-sm text-neutral-900"
          >
            {filteredShortDescription}
          </Text>
        )}

        <Flex
          direction="row"
          justify="between"
          align="center"
          wrap="nowrap"
          className="w-full gap-2"
        >
          <Flex
            direction="row"
            wrap="wrap"
            align="center"
            className="flex-1 gap-4"
          >
            {duration && (
              <Text
                variant="b5"
                tag="span"
                className="inline-flex items-center gap-1.5 text-[12px] font-normal text-neutral"
              >
                <Icon
                  icon="clock-fading"
                  size="sm"
                  className="shrink-0"
                  aria-hidden
                />
                {duration}
              </Text>
            )}
            {flexibility && (
              <Text
                variant="b5"
                tag="span"
                className="inline-flex items-center gap-1.5 text-[12px] font-normal text-neutral"
              >
                <Icon
                  icon="user"
                  size="sm"
                  className="shrink-0"
                  aria-hidden
                  strokeWidth={2}
                />
                {flexibility}
              </Text>
            )}
          </Flex>
          <Button
            ref={ctaRef}
            variant="primaryFill"
            className="shrink-0 border-2 border-brand bg-brand px-5 text-brand-foreground shadow hover:bg-white hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
            onClick={handleProductClick}
            asChild
          >
            <a href={product.productUrl}>{t("learnMore")}</a>
          </Button>
        </Flex>
      </Flex>
    </Card>
  );
}
